import {
  cancelReleaseCriticalPreparation,
  prepareReleaseCriticalOperations
} from '@/services/releaseCriticalOperations'

let preparePromise = null

async function closeApplicationDatabases () {
  try {
    const mod = await import('@/db')
    mod?.db?.close?.({ disableAutoOpen: true })
  } catch (error) {
    console.warn('[PWA release] Dexie close failed:', error)
    throw error
  }
}

function requiresDexieSchemaReset (detail = {}) {
  return String(detail?.reset_scope || '').trim().toLowerCase() === 'dexie-schema'
}

export async function stopApplicationRuntime (detail = {}) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return { ok: true, waiters: 0 }
  }
  const waiters = []
  let acceptingWaiters = true
  const eventDetail = {
    ...(detail && typeof detail === 'object' ? detail : {}),
    waitUntil (promise) {
      if (!acceptingWaiters) {
        throw new Error('agroel-runtime-stop waitUntil() must be called while the event is dispatched.')
      }
      if (!promise || typeof promise.then !== 'function') {
        throw new TypeError('agroel-runtime-stop waitUntil() requires a promise.')
      }
      waiters.push(Promise.resolve(promise))
    }
  }
  try {
    window.dispatchEvent(new CustomEvent('agroel-runtime-stop', {
      detail: eventDetail
    }))
  } finally {
    acceptingWaiters = false
  }
  const results = await Promise.allSettled(waiters)
  const rejected = results.find(result => result.status === 'rejected')
  if (rejected) throw rejected.reason
  return { ok: true, waiters: waiters.length }
}

export function installReleasePrepareHook () {
  if (typeof window === 'undefined') return
  window.__AGROEL_RELEASE_PREPARE__ = async detail => {
    if (preparePromise) return preparePromise
    preparePromise = (async () => {
      const phase = String(detail?.phase || 'quiesce').trim().toLowerCase()
      if (phase === 'cancel') {
        cancelReleaseCriticalPreparation()
        return { ok: true, cancelled: true }
      }
      if (phase === 'commit') {
        window.__AGROEL_RELEASE_RESETTING__ = true
        await stopApplicationRuntime({
          source: 'release-update',
          ...(detail || {})
        })
        const databaseClosed = requiresDexieSchemaReset(detail)
        if (databaseClosed) await closeApplicationDatabases()
        return { ok: true, committed: true, databaseClosed }
      }
      const criticalResult = await prepareReleaseCriticalOperations({ timeoutMs: 10000 })
      if (criticalResult?.ok !== true) {
        cancelReleaseCriticalPreparation()
        return criticalResult
      }
      return { ok: true, quiesced: true }
    })().finally(() => {
      preparePromise = null
    })
    return preparePromise
  }
  window.__AGROEL_RELEASE_CANCEL_PREPARE__ = () => cancelReleaseCriticalPreparation()
}

export function cancelReleasePrepare () {
  cancelReleaseCriticalPreparation()
}

export function checkReleaseNow (reason = 'runtime') {
  const runtime = typeof window !== 'undefined'
    ? window.__AGROEL_RELEASE_RUNTIME__
    : null
  if (!runtime?.check) {
    return Promise.resolve({ ok: false, allowBootstrap: true, reason: 'release-runtime-missing' })
  }
  return runtime.check(reason)
}
